import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ProspectShell } from '../../components/prospect/ProspectShell';

// The public page for a visitor with no session (TYR-208). Scans run by
// invitation, so there is nothing to sign up for here: the page answers what
// OrgIQ is, how a scan handles data, and where to sign in. No hero pitch, no
// form, no funnel steps.
export function ProspectLandingPage() {
  return (
    <ProspectShell>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="max-w-[760px] mx-auto py-14"
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-3">
          OrgIQ by TYRL
        </div>
        <h1 className="text-[38px] font-bold tracking-[-0.03em] leading-tight mb-4">
          A read-only health scan for Salesforce orgs.
        </h1>
        <p className="text-sm text-neutral-400 leading-relaxed">
          OrgIQ scans the configuration of a Salesforce org and reports on automation, data model,
          licences, security and data quality. Scans run by invitation from a TYRL consultant.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <Card title="Have an invite?">
            <p>
              Open the link in your invite email. The link is valid for 24 hours and allows one scan.
            </p>
          </Card>
          <Card title="Consultant or client admin?">
            <p>Sign in to your workspace.</p>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <Link
                to="/hub-login"
                className="inline-flex items-center h-9 px-4 border border-border text-[13px] text-crux-light hover:border-border-strong"
              >
                Sign in
              </Link>
              <Link to="/connect" className="text-[12px] text-neutral-500 underline hover:text-crux-light">
                Consultant sign-in
              </Link>
            </div>
          </Card>
        </div>

        <div className="mt-4 bg-surface-2 border border-border px-6 py-6">
          <h2 className="text-sm font-semibold mb-2.5">How a scan handles data</h2>
          <ul className="space-y-1.5 list-disc pl-4 marker:text-neutral-600 text-[13px] leading-relaxed text-neutral-400">
            <li>You log in on Salesforce&apos;s own page. OrgIQ sends read requests only.</li>
            <li>
              No Salesforce password or token is stored. We ask Salesforce to revoke our access when
              the scan finishes.
            </li>
            <li>
              We keep counts, scores and configuration names. Names of people, emails and file names
              are stripped before anything is stored.
            </li>
          </ul>
          <Link
            to="/privacy"
            className="inline-block mt-4 text-[12px] text-neutral-400 underline hover:text-crux-light"
          >
            Read the full data handling page
          </Link>
        </div>

        <p className="text-[12px] text-neutral-500 mt-6">
          Want a scan for your org? Contact Cyril Duvignau at tyrl.ai.
        </p>
      </motion.div>
    </ProspectShell>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface-2 border border-border px-6 py-5">
      <h2 className="text-sm font-semibold mb-1.5">{title}</h2>
      <div className="text-[13px] text-neutral-400 leading-relaxed">{children}</div>
    </div>
  );
}
